import {
  ArrowRight,
  BarChart3,
  BrainCircuit,
  Check,
  Film,
  Image as ImageIcon,
  Layers3,
  Palette,
  ShieldCheck,
  Sparkles,
  Target,
  WandSparkles,
} from 'lucide-react';
import Reveal from './Reveal';
import './PremiumLanding.css';
import { useSettings } from '../settings';

const CAPABILITIES = [
  'landing.capabilityBrand',
  'landing.capabilityCopy',
  'landing.capabilityImages',
  'landing.capabilityVideo',
  'landing.capabilityExperiments',
  'landing.capabilityScoring',
];

const FEATURES = [
  {
    icon: BrainCircuit,
    labelKey: 'landing.featureBrandLabel',
    titleKey: 'landing.featureBrandTitle',
    copyKey: 'landing.featureBrandCopy',
    className: 'premium-bento-card--wide',
    visual: 'knowledge',
  },
  {
    icon: WandSparkles,
    labelKey: 'landing.featureCampaignLabel',
    titleKey: 'landing.featureCampaignTitle',
    copyKey: 'landing.featureCampaignCopy',
    visual: 'campaign',
  },
  {
    icon: ShieldCheck,
    labelKey: 'landing.featureQualityLabel',
    titleKey: 'landing.featureQualityTitle',
    copyKey: 'landing.featureQualityCopy',
    visual: 'score',
  },
  {
    icon: Palette,
    labelKey: 'landing.featureSuiteLabel',
    titleKey: 'landing.featureSuiteTitle',
    copyKey: 'landing.featureSuiteCopy',
    className: 'premium-bento-card--wide premium-bento-card--dark',
    visual: 'formats',
  },
];

const WORKFLOW = [
  {
    number: '01',
    icon: Layers3,
    titleKey: 'landing.workflowGroundTitle',
    copyKey: 'landing.workflowGroundCopy',
  },
  {
    number: '02',
    icon: Target,
    titleKey: 'landing.workflowChooseTitle',
    copyKey: 'landing.workflowChooseCopy',
  },
  {
    number: '03',
    icon: Sparkles,
    titleKey: 'landing.workflowGenerateTitle',
    copyKey: 'landing.workflowGenerateCopy',
  },
  {
    number: '04',
    icon: BarChart3,
    titleKey: 'landing.workflowReviewTitle',
    copyKey: 'landing.workflowReviewCopy',
  },
];

function CapabilityRail({ t }) {
  const items = [...CAPABILITIES, ...CAPABILITIES];
  return (
    <div className="premium-capability-rail" aria-label={t('landing.capabilitiesLabel')}>
      <div className="premium-capability-track">
        {items.map((item, index) => (
          <span key={`${item}-${index}`} aria-hidden={index >= CAPABILITIES.length}>
            <i><Check size={12} /></i>
            {t(item)}
          </span>
        ))}
      </div>
    </div>
  );
}

function StudioPreview({ t }) {
  return (
    <div className="premium-studio-preview" aria-hidden="true">
      <div className="premium-orbit premium-orbit--one" />
      <div className="premium-orbit premium-orbit--two" />
      <div className="premium-float-chip premium-float-chip--brand"><ShieldCheck size={15} /> {t('landing.previewBrandGrounded')}</div>
      <div className="premium-float-chip premium-float-chip--score"><BarChart3 size={15} /> {t('landing.previewBrandFit')}</div>

      <div className="premium-window">
        <div className="premium-window-bar">
          <div className="premium-window-dots"><i /><i /><i /></div>
          <span>AWN AdPulse / {t('app.workspaceTitle')}</span>
          <div className="premium-live-pill"><i /> {t('landing.previewAiReady')}</div>
        </div>
        <div className="premium-window-body">
          <aside className="premium-window-nav">
            <div className="premium-mini-brand">A</div>
            <i className="active"><WandSparkles size={16} /></i>
            <i><Layers3 size={16} /></i>
            <i><BarChart3 size={16} /></i>
          </aside>
          <div className="premium-window-content">
            <div className="premium-preview-head">
              <div>
                <small>{t('landing.previewCampaignSystem')}</small>
                <strong>{t('landing.previewProductLaunch')}</strong>
              </div>
              <span>{t('landing.previewGenerating')}</span>
            </div>
            <div className="premium-preview-grid">
              <div className="premium-creative premium-creative--portrait">
                <div className="premium-creative-glow" />
                <span>{t('landing.previewStayFlow')}</span>
                <i>{t('landing.previewGlowBottle')}</i>
              </div>
              <div className="premium-preview-side">
                <div className="premium-copy-card">
                  <small>{t('landing.previewCampaignCopy')}</small>
                  <b>{t('landing.previewHydration')}</b>
                  <span /><span /><span className="short" />
                </div>
                <div className="premium-score-card">
                  <div className="premium-score-ring">92</div>
                  <div><small>{t('landing.previewQualityScore')}</small><b>{t('landing.previewReadyRefine')}</b></div>
                </div>
              </div>
            </div>
            <div className="premium-generation-line">
              <span className="done"><Check size={11} /> {t('landing.previewBrief')}</span>
              <span className="done"><Check size={11} /> {t('landing.previewCopy')}</span>
              <span className="active"><Sparkles size={11} /> {t('landing.previewCreative')}</span>
              <span>{t('landing.previewQualityCheck')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureVisual({ type, t }) {
  if (type === 'knowledge') {
    return (
      <div className="premium-feature-visual premium-knowledge-visual" aria-hidden="true">
        <div className="premium-source-card"><span>01</span><b>{t('landing.visualBrandProfile')}</b><i>{t('landing.visualBrandDetails')}</i></div>
        <div className="premium-source-card"><span>02</span><b>{t('landing.visualProductKnowledge')}</b><i>{t('landing.visualProductDetails')}</i></div>
        <div className="premium-source-card"><span>03</span><b>{t('landing.visualApprovedDocs')}</b><i>{t('landing.visualDocDetails')}</i></div>
        <div className="premium-source-merge"><BrainCircuit size={22} /></div>
      </div>
    );
  }
  if (type === 'campaign') {
    return (
      <div className="premium-feature-visual premium-campaign-visual" aria-hidden="true">
        <span><ImageIcon size={14} /> {t('landing.visualVisual')}</span>
        <span><Film size={14} /> {t('landing.visualVideo')}</span>
        <span><Target size={14} /> {t('landing.visualCopy')}</span>
      </div>
    );
  }
  if (type === 'score') {
    return (
      <div className="premium-feature-visual premium-bars-visual" aria-hidden="true">
        <span><i style={{ '--value': '91%' }} /> {t('landing.visualBrandFit')} <b>91</b></span>
        <span><i style={{ '--value': '86%' }} /> {t('landing.visualClarity')} <b>86</b></span>
        <span><i style={{ '--value': '94%' }} /> {t('landing.visualSafety')} <b>94</b></span>
      </div>
    );
  }
  return (
    <div className="premium-feature-visual premium-format-visual" aria-hidden="true">
      <div className="ratio ratio--square">1:1</div>
      <div className="ratio ratio--portrait">4:5</div>
      <div className="ratio ratio--story">9:16</div>
      <span>{t('landing.visualMultiFormat')}</span>
    </div>
  );
}

export default function PremiumLanding({ onStartProject }) {
  const { t } = useSettings();
  return (
    <div className="premium-landing-story">
      <CapabilityRail t={t} />

      <section className="premium-story-section premium-intro-section" aria-labelledby="premium-intro-title">
        <Reveal className="premium-section-copy">
          <p className="premium-kicker"><span /> {t('landing.introKicker')}</p>
          <h2 id="premium-intro-title">{t('landing.introTitle')}</h2>
          <p>
            {t('landing.introCopy')}
          </p>
          <button type="button" className="premium-text-link" onClick={onStartProject}>
            {t('landing.introCta')} <ArrowRight size={16} />
          </button>
        </Reveal>
        <Reveal delay={140} className="premium-preview-wrap">
          <StudioPreview t={t} />
        </Reveal>
      </section>

      <section className="premium-story-section premium-features-section" aria-labelledby="premium-features-title">
        <Reveal className="premium-centered-heading">
          <p className="premium-kicker"><span /> {t('landing.featuresKicker')}</p>
          <h2 id="premium-features-title">{t('landing.featuresTitle')}</h2>
          <p>{t('landing.featuresCopy')}</p>
        </Reveal>
        <div className="premium-bento-grid">
          {FEATURES.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Reveal key={feature.titleKey} delay={index * 70} className={`premium-bento-card ${feature.className || ''}`}>
                <div className="premium-card-icon"><Icon size={19} /></div>
                <p>{t(feature.labelKey)}</p>
                <h3>{t(feature.titleKey)}</h3>
                <span>{t(feature.copyKey)}</span>
                <FeatureVisual type={feature.visual} t={t} />
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="premium-workflow-section" aria-labelledby="premium-workflow-title">
        <div className="premium-story-section">
          <Reveal className="premium-workflow-head">
            <div>
              <p className="premium-kicker premium-kicker--light"><span /> {t('landing.workflowKicker')}</p>
              <h2 id="premium-workflow-title">{t('landing.workflowTitle')}</h2>
            </div>
            <p>{t('landing.workflowCopy')}</p>
          </Reveal>
          <div className="premium-workflow-grid">
            {WORKFLOW.map((item, index) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.number} delay={index * 90} className="premium-workflow-step">
                  <div className="premium-step-top"><span>{item.number}</span><Icon size={19} /></div>
                  <h3>{t(item.titleKey)}</h3>
                  <p>{t(item.copyKey)}</p>
                  {index < WORKFLOW.length - 1 && <i className="premium-step-line" />}
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={220} className="premium-proof-panel">
            <div>
              <small>{t('landing.proofKicker')}</small>
              <strong>{t('landing.proofTitle')}</strong>
            </div>
            <div className="premium-proof-metrics">
              <span><b>3</b> {t('landing.proofFrameworks')}</span>
              <span><b>6</b> {t('landing.proofDimensions')}</span>
              <span><b>1</b> {t('landing.proofSystem')}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="premium-story-section premium-final-pitch" aria-labelledby="premium-final-title">
        <Reveal className="premium-final-card">
          <div className="premium-final-glow" />
          <div>
            <p className="premium-kicker"><span /> {t('landing.finalKicker')}</p>
            <h2 id="premium-final-title">{t('landing.finalTitle')}</h2>
            <p>{t('landing.finalCopy')}</p>
          </div>
          <button type="button" className="premium-final-button" onClick={onStartProject}>
            {t('landing.finalCta')} <ArrowRight size={17} />
          </button>
        </Reveal>
      </section>
    </div>
  );
}
